import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useSearch } from "@tanstack/react-router";

import { PieCountdown } from "@/components";
import { careplanQueryOptions } from "@/domains/careplan/factories";
import { SUBSCRIPTION_PROCESSING_TIMEOUT_MS } from "@/domains/subscriptions/models/constants";
import { useCartStore } from "@/stores";

export const SubscriptionProcessingState = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { subscription_processing } = useSearch({ from: "/_private" });
  const subscriptionProcessingDeadline = useCartStore((state) => {
    return state.subscriptionProcessingDeadline;
  });
  const setSubscriptionProcessingDeadline = useCartStore((state) => {
    return state.setSubscriptionProcessingDeadline;
  });

  const remainingMs = subscriptionProcessingDeadline
    ? Math.max(0, subscriptionProcessingDeadline - Date.now())
    : 0;

  useEffect(() => {
    if (!subscriptionProcessingDeadline) {
      return;
    }

    const timeoutId = setTimeout(async () => {
      setSubscriptionProcessingDeadline(0);
      await queryClient.invalidateQueries({
        queryKey: careplanQueryOptions.current.queryKey,
      });
      if (subscription_processing) {
        const { subscription_processing: _removed, ...searchWithoutParam } = search;
        navigate({
          to: pathname,
          search: searchWithoutParam,
          replace: true,
          resetScroll: false,
        });
      }
    }, remainingMs);

    return () => {
      return clearTimeout(timeoutId);
    };
  }, [
    setSubscriptionProcessingDeadline,
    navigate,
    pathname,
    queryClient,
    search,
    subscription_processing,
    subscriptionProcessingDeadline,
    remainingMs,
  ]);

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6 px-6 py-3.5">
      <div className="flex flex-col items-center justify-center gap-6">
        <PieCountdown
          startMs={subscriptionProcessingDeadline - SUBSCRIPTION_PROCESSING_TIMEOUT_MS}
          totalDurationMs={SUBSCRIPTION_PROCESSING_TIMEOUT_MS}
        />
        <div className="text-center">
          <h2 className="mb-2 text-xl font-semibold text-content">
            Your subscription is being processed
          </h2>
          <p className="text-sm text-content-secondary">
            We&apos;re finalizing your subscription details. Your cart will update automatically
            once it&apos;s ready. This will just take a minute.
          </p>
        </div>
      </div>
    </div>
  );
};
